export class HeifConverter {
    private static _shared: HeifConverter | null = null;
    private libheif: any;
    private scriptLoadPromise: Promise<void> | null = null;

    public static get shared(): HeifConverter {
        if (!HeifConverter._shared) {
            HeifConverter._shared = new HeifConverter();
        }
        return HeifConverter._shared;
    }

    public convert(base64: string): Promise<string> {
        // Cache the load promise: concurrent conversions must await the same
        // in-flight script load, or a second call sees the <script> tag the first call
        // already appended and resolves immediately, before `this.libheif` is set.
        if (!this.scriptLoadPromise) {
            this.scriptLoadPromise = this.loadScript('/vendors/libheif.js');
        }
        return this.scriptLoadPromise.then(() => {
            return this.heifBase64ToJpegDataUrl(base64);
        });
    }

    private async heifBase64ToJpegBlob(heifBase64: string): Promise<Blob> {
        const quality = 0.92;

        // 1) Decode HEIF bytes with libheif (asm.js build)
        const bytes = this.base64ToUint8Array(heifBase64);
        const decoder = new this.libheif.HeifDecoder();
        const images = decoder.decode(bytes.buffer);
        if (!images || !images.length) throw new Error('No HEIF images found');

        const image = images[0];
        const width = image.get_width();
        const height = image.get_height();

        // 2) Draw RGBA pixels into a canvas
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Canvas 2D context unavailable');

        const imageData = ctx.createImageData(width, height);

        await new Promise<void>((resolve, reject) => {
            image.display(imageData, (displayData: ImageData | null) => {
            if (!displayData) return reject(new Error('HEIF processing error'));
            resolve();
            });
        });

        ctx.putImageData(imageData, 0, 0);

        // 3) Encode to JPEG
        const jpegBlob: Blob = await new Promise((resolve, reject) => {
            canvas.toBlob(
            (b) => (b ? resolve(b) : reject(new Error('Failed to encode JPEG'))),
            'image/jpeg',
            quality
            );
        });

        return jpegBlob;

    }

    private async heifBase64ToJpegDataUrl(
        heifBase64: string
    ): Promise<string> {
        const blob = await this.heifBase64ToJpegBlob(heifBase64);
        const fr = new FileReader();
        return await new Promise<string>((resolve, reject) => {
            fr.onload = () => resolve(fr.result as string);
            fr.onerror = () => reject(fr.error);
            fr.readAsDataURL(blob);
        });
    }

    private stripDataUrlPrefix(b64: string): string {
        const i = b64.indexOf(',');
        return i >= 0 ? b64.slice(i + 1) : b64;
    }

    private  base64ToUint8Array(b64: string): Uint8Array {
        const raw = atob(this.stripDataUrlPrefix(b64));
        const out = new Uint8Array(raw.length);
        for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
        return out;
    }

    private loadScript = (url: string): Promise<void> => {
        return new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = url;
            script.async = true;

            script.onload = () => {
                // @ts-ignore
                this.libheif = window.libheif();
                resolve()
            };
            script.onerror = () => reject(new Error(`Failed to load script: ${url}`));

            document.head.appendChild(script);
        });
    }
}